import type { DashboardRevenueProjectionMethodDto } from 'ag-common-lib/lib/contracts/reporting-api';
/* All functionality was moved from revenue-projection-grid component */
/**
 * The three ways the engine can form the year-end level, in accuracy order.
 *
 * `mape`/`worst` are the engine's own walk-forward backtest results (2015-2025 data,
 * targets 2018-2025, as-of weeks 10-40) — see revenue-projection.processor.ts. `runrate`'s pair
 * were measured before it annualised on days (TASKID-3244), which lifted it by ~0.27% against a
 * -6.2% bias; the figures are therefore a shade pessimistic for it, never flattering. They are
 * shown in the picker rather than kept in a doc because `runrate` is a *baseline*, not an
 * alternative: it exists to price the other two, and it under-projects in 31 of 32
 * backtested year-weeks. Offering it without its error next to it would invite someone to
 * drive a bonus payout with the worst estimator on the menu.
 */
export type ProjectionMethodOption = {
  value: DashboardRevenueProjectionMethodDto;
  label: string;
  /** Exact copy shown in the shared hover message. */
  hoverTitle: string;
  hoverHeading: string;
  hoverDescription: string;
  /** one line, shown under the label in the list */
  tagline: string;
  /** mean absolute % error over the backtest */
  mape: number;
  /** worst single year */
  worst: number;
  /** the actual formula the engine evaluates — mirrors levelAt() in the processor */
  formula: string;
  /** detailed calculation context retained for other consumers */
  how: string;
  /** each term of the formula, and where its value comes from */
  terms: { name: string; detail: string }[];
  /** where it breaks down — every method has a failure mode worth naming */
  caveat: string;
};

/**
 * Shared calculation context retained for consumers outside the new hover copy.
 */
export const PROJECTION_DATA_NOTE =
  'Based on premium that has actually settled, week by week, for each carrier. Weeks are counted ' +
  'the same way in every year so they line up. Each carrier row is its share of the overall ' +
  'total, not a separate projection of its own.';

const YTD_TERM = {
  name: 'YTD',
  detail:
    'Premium that has actually settled, from the first week of the year up to the as-of week. That ' +
    'week stops about 3 weeks short of today, because carriers report late — which is why this ' +
    'will not match a raw year-to-date figure.',
};

const F_TERM = {
  name: 'f',
  detail:
    'How much of a typical full year is normally sold by this point. Taken from previous years, ' +
    'leaning hardest on the ones whose week-by-week shape looks most like this year. It follows ' +
    'the real sales curve: roughly 20% of the year lands in Q1, 28% in Q4.',
};

export const PROJECTION_METHOD_OPTIONS: ProjectionMethodOption[] = [
  {
    value: 'credibility',
    label: 'Credibility',
    hoverTitle: 'Credibility',
    hoverHeading: 'Pace Steadied by Long-Term Trends Taking Out Outliers',
    hoverDescription:
      'Uses the long-term production trend from previous years together with the historical pattern for the current month to provide a more balanced estimate.',
    tagline: 'Recommended — this year’s pace, steadied by the long-term trend',
    mape: 2.25,
    worst: 8.5,
    formula: 'projected = YTD + (1−f) × [ YTD + (1−f) × prior ]',
    how:
      'Starts from what this year has actually sold, then leans on the long-term trend to fill in ' +
      'the rest. Early in the year, when there is little to go on, the trend does much of the work ' +
      '— about a third of the answer at midyear. By December this year’s own numbers have taken ' +
      'over almost entirely.',
    terms: [
      YTD_TERM,
      F_TERM,
      {
        name: 'prior',
        detail:
          'Where the long-term trend says this year should land, drawn through past full-year ' +
          'totals. The line is fitted so that a single freak year cannot tilt it. It needs at ' +
          'least 3 complete years of history — with fewer, the projection falls back to Pace.',
      },
    ],
    caveat:
      'The trend cannot know about a change that has never happened before. If this year genuinely ' +
      'breaks from the past, it pulls the estimate toward a history that no longer applies — the ' +
      'cross-check on the total is there to flag exactly that.',
  },
  {
    value: 'pace',
    label: 'Pace',
    hoverTitle: 'Pace',
    hoverHeading: 'Leverage History to Adjust the Run Rate for Monthly Differences',
    hoverDescription:
      'Compares your production at this point in the current month with the same period in previous years to account for normal seasonal ups and downs.',
    tagline: 'This year only — ignores the long-term trend',
    mape: 4.82,
    worst: 16.1,
    formula: 'projected = YTD ÷ f',
    how:
      'Follows the seasonal curve, but looks at nothing beyond this year. Whatever has settled so ' +
      'far is treated as exactly the share of the year it would normally be by now.',
    terms: [YTD_TERM, F_TERM],
    caveat:
      'Tends to come out high — it over-estimated in 7 of the 8 years tested. The business has ' +
      'been booking a little more of its year early over the last decade, and an average of past ' +
      'years is slow to catch up. Useful when you want this year’s evidence on its own.',
  },
  {
    value: 'runrate',
    label: 'Run rate',
    hoverTitle: 'Run Rate',
    hoverHeading: 'Apply Straight Average Production for Remainder of Year',
    hoverDescription:
      'Projects year-end production by extending your current production pace through the remaining days of the year.',
    tagline: 'Simplest — treats every day of the year as the same',
    mape: 7.58,
    worst: 22.4,
    formula: 'projected = YTD ÷ (days elapsed) × (days in the year)',
    how:
      'Takes what has sold so far, works out an average day, and multiplies it out to the full ' +
      'length of the year — 366 days in a leap year, 365 otherwise. It ignores the seasonal curve ' +
      'on purpose — that is the whole point of it. It is the yardstick that shows what allowing ' +
      'for the season and the trend is actually worth.',
    terms: [YTD_TERM],
    caveat:
      'Almost always comes out low — it under-estimated in 31 of the 32 points tested — because ' +
      'the year is weighted toward the back end and an even spread cannot see that. Keep it as a ' +
      'reference point, not a basis for a payout.',
  },
];
